import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, DatePicker, Flex, Form, Input } from 'antd';
import dayjs from 'dayjs';
import FadeIn from '../FadeIn';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useForm from '../../hooks/utility/useForm';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import { BusinessProfileUpdateStrategy } from '../../providers/BusinessProfileProvider';
import useHelpers from '../../hooks/context/useHelpers';
import { getRequiredPersonalFields } from '../../hooks/verification/verificationRules';
import { LegalForm } from '../../services/exports/Constants';
import AddressFields from '../form/address/AddressFields';
import PhoneNumberInput from '../form/general/PhoneNumberInput';

const CONTACT_FIELDS = ['owner_phone_number', 'owner_email'];

/**
 * Get Started's owner's personal information (PROD-559), typed from scratch: nothing entered
 * at creation is shown, because that is what reached the gateways unchecked. Staff type it —
 * Get Started is not opened to owners (Nishita and Artur, 20 Oct). The one exception is the email, which starts as the owner's login email so a
 * typo does not spread to the contract, HubSpot and Adyen (Nishita) — it may still be
 * changed. Once the step has been saved, a return visit shows what was typed.
 *
 * For a GmbH or GbR these are the details of the person who signs the contract.
 */
export default function OwnerPersonalInfoStep({ buttonText, moveBackwards, moveForward }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:OwnerPersonalInfoStep' });

  const { company } = useSelector((state) => state.currentCompany);
  const { business_profile, getValue, updating, pushUpdates } = useBusinessProfile();
  const { dateInputFormat } = useHelpers();
  const { setErrorFeedback, renderFeedbackLabel } = useFeedbackLabel({ t });

  // The restaurant's own stamp — the provider's getValue() would read a sibling's through
  // the shared master.
  const hasConfirmed = Boolean(company?.business_profile?.personal_info_confirmed_at);
  // The provider hands over the group's master, which holds the owner account.
  const loginEmail = business_profile?.owner?.email ?? null;
  const requiredFields = [...getRequiredPersonalFields(getValue('legal_country')), ...CONTACT_FIELDS];

  const { data, update } = useForm({
    ...Object.fromEntries(requiredFields.map((field) => [field, hasConfirmed ? (getValue(field) ?? null) : null])),
    owner_email: hasConfirmed ? (getValue('owner_email') ?? null) : loginEmail,
  });

  const isComplete = requiredFields.every((field) => Boolean(data[field]?.trim?.() ?? data[field]));
  const legalForm = getValue('legal_form');

  // Once per owner, as Business details (Bohdan): saved for every restaurant of the group.
  const save = () =>
    isComplete &&
    pushUpdates({
      data: { ...data, personal_info_confirmed: true },
      updateStrategy: BusinessProfileUpdateStrategy.All,
      onSuccess: () => moveForward && moveForward(),
      onError: ({ data: response }) => setErrorFeedback(response),
    });

  return (
    <FadeIn>
      {renderFeedbackLabel}
      <p className="text-gray-500 text-sm mb-md">
        {t(`intro.${[LegalForm.Gbr, LegalForm.Gmbh].includes(legalForm) ? legalForm : 'default'}`)}
      </p>
      <Form layout="vertical">
        <Form.Item label={t('form.owner_first_name.label')} required>
          <Input
            placeholder={t('form.owner_first_name.placeholder')}
            value={data.owner_first_name ?? ''}
            onChange={(e) => update('owner_first_name', e.target.value)}
            data-testid="owner-first-name"
          />
        </Form.Item>
        <Form.Item label={t('form.owner_last_name.label')} required>
          <Input
            placeholder={t('form.owner_last_name.placeholder')}
            value={data.owner_last_name ?? ''}
            onChange={(e) => update('owner_last_name', e.target.value)}
            data-testid="owner-last-name"
          />
        </Form.Item>
        <Form.Item label={t('form.owner_dob.label')} required>
          <DatePicker
            format={[dateInputFormat, 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY']}
            value={data.owner_dob ? dayjs(data.owner_dob) : null}
            placeholder={t('form.owner_dob.placeholder')}
            onChange={(value) => update('owner_dob', value ? value.utc(true).toISOString() : null)}
            data-testid="owner-dob"
          />
        </Form.Item>
        <Form.Item label={t('form.owner_phone_number.label')} required>
          <PhoneNumberInput
            defaultCountry={getValue('legal_country')}
            value={data.owner_phone_number ?? ''}
            onChange={(value) => update('owner_phone_number', value)}
            international
            className="w-full font-sm relative"
            countryCallingCodeEditable={false}
          />
        </Form.Item>
        <Form.Item label={t('form.owner_email.label')} extra={t('form.owner_email.help')} required>
          <Input
            value={data.owner_email ?? ''}
            onChange={(e) => update('owner_email', e.target.value)}
            placeholder={t('form.owner_email.placeholder')}
            type="email"
            data-testid="owner-email"
          />
        </Form.Item>
        {requiredFields.includes('owner_id_number') && (
          <Form.Item label={t('form.owner_id_number.label')} required>
            <Input
              value={data.owner_id_number ?? ''}
              placeholder={t('form.owner_id_number.placeholder')}
              onChange={(e) => update('owner_id_number', e.target.value)}
            />
          </Form.Item>
        )}
        <hr />
        <h6 className="mt-md">{t('sections.home_address')}</h6>
        <p className="text-gray-500 text-sm">{t('sections.home_address_help')}</p>
        <AddressFields prefix="owner" data={data} onChange={update} useGrid className="mt-md" />
      </Form>
      <Flex justify="end">
        {moveBackwards && (
          <Button onClick={moveBackwards} className="mr-md">
            {t('buttons.back')}
          </Button>
        )}
        <Button
          type="primary"
          onClick={save}
          loading={updating}
          disabled={!isComplete}
          data-testid="owner-personal-info-save"
        >
          {buttonText ?? t('buttons.next')}
        </Button>
      </Flex>
    </FadeIn>
  );
}
